import { useEffect, useState } from "react";
import { Gift } from "lucide-react";
import api from "../lib/api";
import { Card, ErrorBox, Loading, PageTitle, Stat } from "./ui";
import { fmtDate, win } from "../lib/format";

export default function Rewards() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [claim, setClaim] = useState({ state: "idle", message: "" });

  const load = () =>
    Promise.all([api.get("/rewards/status"), api.get("/wallet/transactions")])
      .then(([status, txns]) =>
        setData({
          status: status.data,
          history: (txns.data?.transactions || txns.data || []).filter((t) =>
            /reward|claim|daily/i.test(t.type || "")
          ),
        })
      )
      .catch(() => setError("Could not load your rewards. Please refresh."));

  useEffect(() => {
    load();
  }, []);

  const claimReward = async () => {
    setClaim({ state: "sending", message: "" });
    try {
      const r = await api.post("/rewards/claim");
      const got = r.data?.claimed ?? r.data?.amount ?? r.data?.reward;
      setClaim({ state: "done", message: got != null ? `Claimed ${win(got)} successfully.` : "Daily reward claimed successfully." });
      await load();
    } catch (err) {
      setClaim({ state: "error", message: err.response?.data?.detail || "Could not claim right now. Please try again later." });
    }
  };

  if (error) return <ErrorBox message={error} />;
  if (!data) return <Loading label="Loading your rewards" />;

  const { status, history } = data;
  const next = status.next_tier;

  return (
    <div data-testid="rewards-page">
      <PageTitle title="Rewards" sub="Your daily reward status and claim history." />

      <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat gold testId="rewards-daily" label="Daily Reward" value={win(status.daily_reward)} sub={status.tier_label} />
        <Stat testId="rewards-streak" label="Current Streak" value={`${status.streak || 0} days`} />
        <Stat testId="rewards-monthly" label="Monthly Estimate" value={win(status.monthly_estimate)} sub="estimate, not guaranteed" />
        <Stat testId="rewards-can-claim" label="Today's Claim" value={status.can_claim ? "Available" : "Claimed"} sub={status.can_claim ? "Claim before midnight" : "Come back tomorrow"} />
      </div>

      <Card className="mt-6" data-testid="claim-card">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/[0.07] p-3 text-[#D4AF37]">
              <Gift className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold text-white">Claim Daily Reward</h2>
              <p className="mt-1 text-sm text-[#99A1AF]">
                Claim every day to build your streak and grow your tier.
              </p>
              {next && (
                <p className="mt-2 font-mono text-[11px] text-[#636B78]">
                  Next tier: {next.label} · {win(next.daily_reward)}/day at {next.min} WIN
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col items-start gap-3">
            <button
              onClick={claimReward}
              disabled={!status.can_claim || claim.state === "sending"}
              data-testid="claim-reward-button"
              className="rounded-full bg-[#D4AF37] px-7 py-3 text-sm font-semibold text-[#08080A] transition-colors hover:bg-[#F3E5AB] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {claim.state === "sending" ? "Claiming…" : status.can_claim ? "Claim Now" : "Already Claimed"}
            </button>
            {claim.message && (
              <p className={`text-xs ${claim.state === "error" ? "text-red-400" : "text-emerald-400"}`} data-testid="claim-status">
                {claim.message}
              </p>
            )}
          </div>
        </div>
      </Card>

      <Card className="mt-6" data-testid="rewards-history-card">
        <h2 className="font-display text-lg font-semibold text-white">Reward History</h2>
        {history.length === 0 ? (
          <p className="mt-4 text-sm text-[#636B78]">No reward claims yet. Your claims will appear here.</p>
        ) : (
          <div className="mt-4 divide-y divide-white/[0.06]">
            {history.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm text-white">{t.description || "Daily reward"}</p>
                  <p className="mt-0.5 font-mono text-[11px] text-[#636B78]">{fmtDate(t.created_at)}</p>
                </div>
                <span className="font-mono text-sm font-semibold text-emerald-400">+{win(t.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
